import React, { useState, useEffect } from 'react';
import {
  History, Search, Filter, RefreshCw, Download, FileSpreadsheet,
  FileText, Printer, User, School, Calendar, Award,
  CheckCircle, AlertTriangle, ChevronLeft, ChevronRight, X, BarChart3, TrendingUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const HistoricalDataPage: React.FC = () => {
  const { fetchWithAuth, user } = useAuth();

  // Filters
  const [academicSession, setAcademicSession] = useState('all');
  const [month, setMonth] = useState('all');
  const [block, setBlock] = useState('all');
  const [schoolId, setSchoolId] = useState('all');
  const [classId, setClassId] = useState('all');
  const [section, setSection] = useState('all');
  const [stream, setStream] = useState('all');
  const [subjectId, setSubjectId] = useState('all');
  const [gender, setGender] = useState('all');
  const [srn, setSrn] = useState('');
  const [search, setSearch] = useState('');

  // Data state
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({
    total_records: 0,
    evaluated_count: 0,
    absent_count: 0,
    average_percentage: 0,
    pass_count: 0
  });

  // Reference options
  const [sessions, setSessions] = useState<any[]>([]);
  const [months, setMonths] = useState<any[]>([]);
  const [schools, setSchools] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Student 360 Modal
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [studentProfile, setStudentProfile] = useState<any | null>(null);
  const [loadingStudent, setLoadingStudent] = useState(false);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchHistoricalRecords();
  }, [page, academicSession, month, block, schoolId, classId, section, stream, subjectId, gender]);

  const fetchReferenceData = async () => {
    try {
      const [sessRes, monthRes, schoolRes, subRes] = await Promise.all([
        fetchWithAuth('/api/academic-sessions'),
        fetchWithAuth('/api/monthly-assessments/months'),
        fetchWithAuth('/api/schools'),
        fetchWithAuth('/api/subjects')
      ]);

      if (sessRes.ok) {
        const d = await sessRes.json();
        setSessions(d.sessions || d.data || []);
      }
      if (monthRes.ok) {
        const d = await monthRes.json();
        setMonths(d.data || d.months || []);
      }
      if (schoolRes.ok) {
        const d = await schoolRes.json();
        setSchools(d.schools || d.data || []);
      }
      if (subRes.ok) {
        const d = await subRes.json();
        setSubjects(d.subjects || d.data || []);
      }
    } catch (e) {
      console.error('Error loading reference options:', e);
    }
  };

  const fetchHistoricalRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      if (academicSession !== 'all') params.append('academic_session_id', academicSession);
      if (month !== 'all') params.append('month', month);
      if (block !== 'all') params.append('block', block);
      if (schoolId !== 'all') params.append('school_id', schoolId);
      if (classId !== 'all') params.append('class_id', classId);
      if (section !== 'all') params.append('section', section);
      if (stream !== 'all') params.append('stream', stream);
      if (subjectId !== 'all') params.append('subject_id', subjectId);
      if (gender !== 'all') params.append('gender', gender);
      if (srn.trim()) params.append('srn', srn.trim());
      if (search.trim()) params.append('search', search.trim());

      const res = await fetchWithAuth(`/api/historical/records?${params.toString()}`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          setRecords(json.data.records || []);
          setSummary(json.data.summary || {});
          setTotalCount(json.data.pagination?.total || 0);
          setTotalPages(json.data.pagination?.totalPages || 1);
        }
      }
    } catch (e) {
      console.error('Error fetching historical records:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchHistoricalRecords();
  };

  const handleReset = () => {
    setAcademicSession('all');
    setMonth('all');
    setBlock('all');
    setSchoolId('all');
    setClassId('all');
    setSection('all');
    setStream('all');
    setSubjectId('all');
    setGender('all');
    setSrn('');
    setSearch('');
    setPage(1);
  };

  const handleExport = (format: 'xlsx' | 'csv') => {
    const params = new URLSearchParams();
    params.append('format', format);
    params.append('level', block !== 'all' ? 'block' : schoolId !== 'all' ? 'school' : 'district');
    if (academicSession !== 'all') params.append('academic_session_id', academicSession);
    if (month !== 'all') params.append('month', month);
    if (block !== 'all') params.append('block', block);
    if (schoolId !== 'all') params.append('school_id', schoolId);
    if (classId !== 'all') params.append('class_id', classId);
    if (subjectId !== 'all') params.append('subject_id', subjectId);

    window.open(`/api/historical/export?${params.toString()}`, '_blank');
  };

  const handlePrint = () => {
    window.print();
  };

  const openStudentHistory = async (studentId: number) => {
    setSelectedStudentId(studentId);
    setLoadingStudent(true);
    try {
      const res = await fetchWithAuth(`/api/historical/student/${studentId}`);
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setStudentProfile(json.data);
        }
      }
    } catch (e) {
      console.error('Error loading student profile:', e);
    } finally {
      setLoadingStudent(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Official Government Header */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-50 text-[#0F2C59] rounded-xl border border-blue-200 shadow-inner">
            <History className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">
                District Historical Assessment Archives
              </h1>
              <span className="bg-emerald-100 text-emerald-900 text-[10px] font-bold px-2 py-0.5 rounded uppercase border border-emerald-300">
                Multi-Year Archive
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Permanent chronological repository covering Academic Year → Assessment Month → Block → School → Class → Subject → Student marks.
            </p>
          </div>
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('xlsx')}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#0F2C59] text-white rounded-lg hover:bg-[#1E40AF] transition text-xs font-semibold shadow-sm"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span>Excel Export</span>
          </button>

          <button
            onClick={() => handleExport('csv')}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition text-xs font-semibold shadow-sm"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>CSV Export</span>
          </button>

          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white text-slate-700 rounded-lg hover:bg-slate-50 transition text-xs font-semibold shadow-sm"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Filtered Records</div>
          <div className="text-2xl font-black text-[#0F2C59] mt-1">{totalCount.toLocaleString()}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Historical Entries</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Evaluated Count</div>
          <div className="text-2xl font-black text-emerald-700 mt-1">{summary.evaluated_count?.toLocaleString() || 0}</div>
          <div className="text-[10px] text-emerald-600 mt-0.5">Appeared for Exams</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Average Percentage</div>
          <div className="text-2xl font-black text-blue-700 mt-1">{summary.average_percentage || 0}%</div>
          <div className="text-[10px] text-blue-600 mt-0.5">Mean Performance</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Passing Records</div>
          <div className="text-2xl font-black text-indigo-700 mt-1">{summary.pass_count?.toLocaleString() || 0}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Score ≥ 33%</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm col-span-2 lg:col-span-1">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Absent Entries</div>
          <div className="text-2xl font-black text-amber-700 mt-1">{summary.absent_count?.toLocaleString() || 0}</div>
          <div className="text-[10px] text-amber-600 mt-0.5">Marked Absent</div>
        </div>
      </div>

      {/* Advanced Filter Matrix */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
        <form onSubmit={handleSearchSubmit} className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-slate-200">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider">
              <Filter className="w-3.5 h-3.5 text-[#0F2C59]" />
              <span>Multi-Level Historical Hierarchy Filter</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="submit"
                className="px-4 py-1.5 bg-[#0F2C59] text-white text-xs font-bold rounded-lg hover:bg-[#1E40AF] transition"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-3 py-1.5 border border-slate-300 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
            {/* Academic Year */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Academic Year</label>
              <select
                value={academicSession}
                onChange={e => { setAcademicSession(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium"
              >
                <option value="all">All Academic Years</option>
                {sessions.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Assessment Month */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Assessment Month</label>
              <select
                value={month}
                onChange={e => { setMonth(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium"
              >
                <option value="all">All Months</option>
                {months.map(m => (
                  <option key={m.id} value={m.month_name}>{m.month_name}</option>
                ))}
              </select>
            </div>

            {/* Block */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Block</label>
              <select
                value={block}
                onChange={e => { setBlock(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium"
              >
                <option value="all">All 5 Blocks</option>
                <option value="Nuh">Nuh</option>
                <option value="Nagina">Nagina</option>
                <option value="Taoru">Taoru</option>
                <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
                <option value="Punhana">Punhana</option>
              </select>
            </div>

            {/* School */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">School</label>
              <select
                value={schoolId}
                onChange={e => { setSchoolId(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium truncate"
              >
                <option value="all">All 161 Schools</option>
                {schools.map(sc => (
                  <option key={sc.id} value={sc.id}>{sc.school_name} ({sc.school_code})</option>
                ))}
              </select>
            </div>

            {/* Class */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Class</label>
              <select
                value={classId}
                onChange={e => { setClassId(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium"
              >
                <option value="all">All Classes</option>
                <option value="1">Class 10</option>
                <option value="2">Class 12</option>
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Subject</label>
              <select
                value={subjectId}
                onChange={e => { setSubjectId(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium"
              >
                <option value="all">All Subjects</option>
                {subjects.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-xs pt-1">
            {/* Search by student name / school */}
            <div>
              <input
                type="text"
                placeholder="Search student name, school..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 text-xs"
              />
            </div>

            {/* SRN */}
            <div>
              <input
                type="text"
                placeholder="Exact Student SRN..."
                value={srn}
                onChange={e => setSrn(e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 text-xs"
              />
            </div>

            {/* Gender */}
            <div>
              <select
                value={gender}
                onChange={e => { setGender(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 text-xs"
              >
                <option value="all">All Genders</option>
                <option value="Male">Boys (Male)</option>
                <option value="Female">Girls (Female)</option>
              </select>
            </div>

            {/* Stream */}
            <div>
              <select
                value={stream}
                onChange={e => { setStream(e.target.value); setPage(1); }}
                className="w-full p-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-800 text-xs"
              >
                <option value="all">All Streams</option>
                <option value="Science">Science</option>
                <option value="Commerce">Commerce</option>
                <option value="Arts">Arts</option>
                <option value="General">General</option>
              </select>
            </div>
          </div>
        </form>
      </div>

      {/* Main Historical Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
          <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Chronological Assessment Records ({totalCount.toLocaleString()} Entries)
          </div>
          <div className="text-xs text-slate-500 font-medium">
            Page {page} of {totalPages}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-700">
            <thead className="bg-slate-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="p-3">Academic Year</th>
                <th className="p-3">Month</th>
                <th className="p-3">Block</th>
                <th className="p-3">School Name</th>
                <th className="p-3">Class / Sec</th>
                <th className="p-3">Student Name</th>
                <th className="p-3">SRN</th>
                <th className="p-3">Subject</th>
                <th className="p-3 text-center">Marks</th>
                <th className="p-3 text-center">Max</th>
                <th className="p-3 text-center">%</th>
                <th className="p-3 text-right">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-slate-500">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-400" />
                    Searching historical records...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-slate-500">
                    No historical records found for selected criteria.
                  </td>
                </tr>
              ) : (
                records.map(r => (
                  <tr key={r.mark_id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="p-3 font-semibold text-slate-900">{r.academic_year}</td>
                    <td className="p-3 font-medium text-slate-700">{r.assessment_month}</td>
                    <td className="p-3 text-slate-600">{r.block}</td>
                    <td className="p-3 font-medium text-slate-900 max-w-xs truncate" title={r.school_name}>
                      {r.school_name}
                    </td>
                    <td className="p-3 text-slate-700 font-medium">
                      {r.class_name} - {r.section}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => openStudentHistory(r.student_id)}
                        className="font-bold text-[#0F2C59] hover:text-blue-700 hover:underline text-left"
                      >
                        {r.student_name}
                      </button>
                    </td>
                    <td className="p-3 font-mono text-[11px] text-slate-500">{r.srn}</td>
                    <td className="p-3 font-medium text-slate-800">{r.subject_name}</td>
                    <td className="p-3 text-center font-bold text-slate-900">
                      {r.is_absent === 1 ? (
                        <span className="text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded text-[10px] font-bold">AB</span>
                      ) : (
                        r.obtained_marks
                      )}
                    </td>
                    <td className="p-3 text-center text-slate-500">{r.maximum_marks}</td>
                    <td className="p-3 text-center font-bold">
                      {r.is_absent === 1 ? '—' : `${r.percentage}%`}
                    </td>
                    <td className="p-3 text-right">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        r.is_absent === 1
                          ? 'bg-amber-100 text-amber-800'
                          : r.percentage >= 33
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}>
                        {r.is_absent === 1 ? 'Absent' : r.percentage >= 33 ? 'Pass' : 'Needs Support'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-xs text-slate-500">
            Showing Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({totalCount.toLocaleString()} total entries)
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Student 360 History Modal */}
      {selectedStudentId && studentProfile && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-[#0F2C59] p-4 text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-amber-400" />
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {studentProfile.student?.student_name} — Complete Academic History
                  </h3>
                  <div className="text-[11px] text-slate-300">
                    SRN: {studentProfile.student?.srn} • {studentProfile.student?.school_name} ({studentProfile.student?.block})
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setSelectedStudentId(null); setStudentProfile(null); }}
                className="text-slate-300 hover:text-white font-bold p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Subject Breakdown */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {studentProfile.performanceSummary?.map((ps: any, idx: number) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-[11px] font-bold text-slate-600 truncate">{ps.subject}</div>
                    <div className="text-lg font-black text-[#0F2C59] mt-0.5">{ps.average_percentage}%</div>
                    <div className="text-[10px] text-slate-400">{ps.evaluations_count} evaluations</div>
                  </div>
                ))}
              </div>

              {/* Chronological Table */}
              <div>
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">
                  Chronological Evaluation Log
                </h4>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 font-bold text-slate-700">
                      <tr>
                        <th className="p-2.5">Academic Year</th>
                        <th className="p-2.5">Month</th>
                        <th className="p-2.5">Subject</th>
                        <th className="p-2.5 text-center">Marks</th>
                        <th className="p-2.5 text-center">Max Marks</th>
                        <th className="p-2.5 text-center">Percentage</th>
                        <th className="p-2.5 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {studentProfile.history?.map((h: any, idx: number) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2.5 font-semibold">{h.academic_year}</td>
                          <td className="p-2.5 font-medium">{h.assessment_month}</td>
                          <td className="p-2.5 font-bold text-slate-900">{h.subject_name}</td>
                          <td className="p-2.5 text-center font-bold">
                            {h.is_absent === 1 ? 'AB' : h.obtained_marks}
                          </td>
                          <td className="p-2.5 text-center text-slate-500">{h.maximum_marks}</td>
                          <td className="p-2.5 text-center font-bold">
                            {h.is_absent === 1 ? '—' : `${h.percentage}%`}
                          </td>
                          <td className="p-2.5 text-right">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              h.is_absent === 1
                                ? 'bg-amber-100 text-amber-800'
                                : h.percentage >= 33
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}>
                              {h.is_absent === 1 ? 'Absent' : h.percentage >= 33 ? 'Passed' : 'Needs Support'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalDataPage;
